import { useSetAtom } from 'jotai';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { useCallback, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { selectWidgetAIAtom } from '~/atoms/dashboardAtom';
import { usePopout } from '~/contexts/PopoutContext';
import { WidgetActions } from '../common/WidgetActions';

export type DynamicHistogramWidgetProps<
  T extends Record<string, unknown> = Record<string, unknown>,
> = {
  data: T[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  // Function to map each data item to { name, value }
  mapBar: (item: T) => { name: string; value: number };
};

export const DynamicHistogramWidget = <
  T extends Record<string, unknown> = Record<string, unknown>,
>({
  data,
  isLoading,
  error,
  refetch,
  handleRemoveWidget,
  widgetId,
  title = 'Dynamic Histogram',
  mapBar,
}: DynamicHistogramWidgetProps<T>) => {
  const [parseError, setParseError] = useState<Error | null>(null);
  const setSelectWidgetAI = useSetAtom(selectWidgetAIAtom);
  const { openPopout } = usePopout();

  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );

  const handlePopout = useCallback(() => {
    openPopout(
      widgetId,
      {
        type: 'DynamicHistogramWidget',
        props: {
          data,
          isLoading,
          error,
          refetch,
          handleRemoveWidget,
          widgetId,
          title,
          mapBar,
        },
      },
      {
        width: 900,
        height: 600,
        title: title,
      },
    );
  }, [
    data,
    isLoading,
    error,
    refetch,
    handleRemoveWidget,
    widgetId,
    title,
    mapBar,
    openPopout,
  ]);

  const chartData = useMemo(() => {
    try {
      return data.map(mapBar);
    } catch (error) {
      setParseError(error as Error);
      return [];
    }
  }, [data, mapBar]);

  const noData = useMemo(() => {
    return !chartData.length || chartData.every((entry) => entry.value === 0);
  }, [chartData]);

  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full" />
      </div>
    );
  }

  if (error || parseError) {
    return (
      <div className="hover-container h-full">
        <div className="w-full flex justify-content-between align-items-center">
          <h3 className="mb-0">{title}</h3>
          <div className="align-items-center flex flex-row align-self-start">
            <WidgetActions
              onRemove={removeWidget}
              onPopout={handlePopout}
              widgetId={widgetId}
            />
          </div>
        </div>
        <div
          className="flex justify-content-center align-items-center"
          style={{ height: 'calc(100% - 3.4rem)' }}
        >
          <div className="p-error">Something went wrong.</div>
        </div>
      </div>
    );
  }

  if (noData) {
    return (
      <div className="hover-container h-full">
        <div className="w-full flex justify-content-between align-items-center">
          <h3 className="mb-0">{title}</h3>
          <div className="align-items-center flex flex-row align-self-start">
            <WidgetActions
              onRemove={removeWidget}
              onPopout={handlePopout}
              widgetId={widgetId}
            />
          </div>
        </div>
        <div
          className="flex justify-content-center align-items-center"
          style={{ height: 'calc(100% - 3.4rem)' }}
        >
          <div>No data found.</div>
        </div>
      </div>
    );
  }

  const yAxisUpperLimit = Math.max(...chartData.map((item) => item.value));

  return (
    <div className="hover-container h-full" id={widgetId}>
      <div className="flex justify-content-between align-items-center">
        <div className="w-full flex justify-content-between align-items-center">
          <div className="flex align-items-center gap-3">
            <h3 className="mb-0">{title}</h3>
          </div>
          <div className="align-items-center flex flex-row align-self-start">
            <WidgetActions
              onRemove={removeWidget}
              onRefresh={() => refetch()}
              onAddToChat={setSelectWidgetAI}
              onPopout={handlePopout}
              widgetId={widgetId}
            />
          </div>
        </div>
      </div>
      <div
        className="mt-3"
        style={{ height: 'calc(100% - 3.4rem)', marginLeft: '0' }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" angle={-0} textAnchor="end" dy={10} />
            <YAxis domain={[0, yAxisUpperLimit + (1 / 5) * yAxisUpperLimit]} />
            <Tooltip formatter={(value) => Number(value).toLocaleString()} />
            <Bar dataKey="value" fill="#8884d8" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
